import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

interface AdditionalDetailsSectionProps {
    additionalAttributes: Record<string, string> | null | undefined;
}

/**
 * Renders the "Additional Details" section of the Work Item Detail Page.
 *
 * - Always renders the "Additional Details" header.
 * - When `additionalAttributes` is null, undefined, or an empty object,
 *   renders a single placeholder message.
 * - Otherwise renders one key/value row per map entry, displaying the raw
 *   key and raw value as text. Iteration order follows `Object.entries`
 *   (insertion order in modern engines).
 */
const AdditionalDetailsSection: React.FC<AdditionalDetailsSectionProps> = ({
    additionalAttributes,
}) => {
    const entries =
        additionalAttributes != null ? Object.entries(additionalAttributes) : [];

    return (
        <Column spacing="300">
            <Heading level={2}>Additional Details</Heading>
            {entries.length === 0 ? (
                <Text type="b200" data-testid="additional-details-empty">
                    No additional details available
                </Text>
            ) : (
                entries.map(([key, value]) => (
                    <Box key={key} data-testid="additional-details-row">
                        <Row
                            spacing="300"
                            alignmentVertical="top"
                            widths={['200px', 'fill']}
                        >
                            <Text
                                type="b200"
                                color="secondary"
                                data-testid="additional-details-key"
                            >
                                {key}
                            </Text>
                            <Text
                                type="b200"
                                data-testid="additional-details-value"
                            >
                                {value}
                            </Text>
                        </Row>
                    </Box>
                ))
            )}
        </Column>
    );
};

export default AdditionalDetailsSection;
